type Range = readonly [from: number, to: number]

const EN_LAYOUT = "qwertyuiop[]asdfghjkl;'zxcvbnm,."
const RU_LAYOUT = 'йцукенгшщзхъфывапролджэячсмитьбю'

/** Нижний регистр и «ё» → «е» посимвольно: длина строки не меняется, смещения совпадают с исходным текстом. */
function fold(value: string): string {
  return value.replace(/[\s\S]/g, (char) => {
    const lower = char.toLowerCase()
    if (lower.length !== 1) return char
    return lower === 'ё' ? 'е' : lower
  })
}

/** Слово, набранное не в той раскладке («ghbdtn» ↔ «привет»); null — если не набрано целиком одной раскладкой. */
function otherLayout(word: string): string | null {
  const toRu = [...word].every((char) => EN_LAYOUT.includes(char))
  const source = toRu ? EN_LAYOUT : RU_LAYOUT
  const target = toRu ? RU_LAYOUT : EN_LAYOUT
  if (!toRu && ![...word].every((char) => RU_LAYOUT.includes(char))) return null
  return [...word].map((char) => target[source.indexOf(char)]).join('')
}

function commonPrefix(a: string, b: string): number {
  let length = 0
  while (length < a.length && length < b.length && a[length] === b[length]) length++
  return length
}

/** Соседние номера символов → диапазоны. */
function toRanges(indexes: number[]): Range[] {
  const ranges: [number, number][] = []
  for (const index of indexes) {
    const last = ranges[ranges.length - 1]
    if (last !== undefined && last[1] === index) last[1] = index + 1
    else ranges.push([index, index + 1])
  }
  return ranges
}

/** Буквы слова, оставшиеся после выравнивания с запросом по расстоянию Левенштейна; null — слишком далеко. */
function alignByEdits(query: string, word: string, allowed: number): Range[] | null {
  const rows = query.length + 1
  const cols = word.length + 1
  const distance = Array.from({ length: rows }, (_, row) => Array.from({ length: cols }, (_, col) => (row === 0 ? col : col === 0 ? row : 0)))
  for (let row = 1; row < rows; row++) {
    for (let col = 1; col < cols; col++) {
      const substitution = distance[row - 1]![col - 1]! + (query[row - 1] === word[col - 1] ? 0 : 1)
      distance[row]![col] = Math.min(substitution, distance[row - 1]![col]! + 1, distance[row]![col - 1]! + 1)
    }
  }
  if (distance[rows - 1]![cols - 1]! > allowed) return null
  const matched: number[] = []
  let row = rows - 1
  let col = cols - 1
  while (row > 0 && col > 0) {
    const here = distance[row]![col]!
    if (query[row - 1] === word[col - 1] && here === distance[row - 1]![col - 1]!) {
      matched.push(col - 1)
      row--
      col--
    } else if (here === distance[row - 1]![col - 1]! + 1) {
      row--
      col--
    } else if (here === distance[row]![col - 1]! + 1) col--
    else row--
  }
  return toRanges(matched.reverse())
}

/**
 * Какие буквы слова совпали со словом запроса. Три признака, от сильного к слабому:
 *  1. общее начало — основа слова при разных окончаниях («животное» ↔ «живот»);
 *  2. близкое написание (слова от пяти букв) — опечатка в одну-три буквы, подсвечиваются только совпавшие буквы;
 *  3. иначе совпадения нет (смысловое совпадение векторного поиска буквами не объяснить).
 */
function matchWord(query: string, word: string): Range[] | null {
  const prefix = commonPrefix(query, word)
  if (prefix >= Math.max(3, Math.ceil(query.length * 0.6))) return [[0, prefix]]
  if (query.length < 5 || word.length < 5) return null
  return alignByEdits(query, word, query.length <= 6 ? 1 : query.length <= 10 ? 2 : 3)
}

/**
 * Части текста: что в нём совпало с запросом (без учёта регистра) и всё между ними. Совпадение — это
 * и точная подстрока слова запроса, и общая основа слова, и слово с опечаткой, и слово, набранное в другой раскладке.
 */
export function highlightParts(text: string, query: string): { text: string; match: boolean }[] {
  const tokens = [...new Set(fold(query).split(/\s+/).filter((token) => token !== ''))]
  if (tokens.length === 0 || text === '') return [{ text, match: false }]
  const haystack = fold(text)
  const marked = new Array<boolean>(text.length).fill(false)
  const mark = (from: number, to: number) => marked.fill(true, from, to)

  for (const token of tokens) {
    for (let at = haystack.indexOf(token); at !== -1; at = haystack.indexOf(token, at + token.length)) mark(at, at + token.length)
  }

  const words = [...haystack.matchAll(/\p{L}+/gu)]
  const queryWords = new Set<string>()
  for (const token of tokens) {
    for (const word of token.match(/\p{L}+/gu) ?? []) {
      queryWords.add(word)
      const swapped = otherLayout(word)
      if (swapped !== null) queryWords.add(swapped)
    }
  }
  for (const queryWord of queryWords) {
    for (const word of words) {
      const ranges = matchWord(queryWord, word[0])
      for (const [from, to] of ranges ?? []) mark(word.index + from, word.index + to)
    }
  }

  const parts: { text: string; match: boolean }[] = []
  let start = 0
  for (let index = 1; index <= text.length; index++) {
    if (index === text.length || marked[index] !== marked[start]) {
      parts.push({ text: text.slice(start, index), match: marked[start]! })
      start = index
    }
  }
  return parts
}
